import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { useLang } from '../context/LangContext';
import {
  FaExternalLinkAlt, FaArrowLeft, FaEye, FaCalendarAlt,
  FaFolder, FaGlobe, FaChevronRight, FaTag, FaArrowRight
} from 'react-icons/fa';

function ResourceDetail() {
  const { slug } = useParams();
  const { langParam, t } = useLang();
  const [resource, setResource] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    const params = langParam ? { lang: langParam } : {};
    api.get(`/resources/${slug}`, { params })
      .then(res => {
        // Support both old format (flat) and new format ({resource, related})
        if (res.data.resource) {
          setResource(res.data.resource);
          setRelated(res.data.related || []);
        } else {
          setResource(res.data);
          setRelated([]);
        }
      })
      .catch(() => setError('Resource not found.'))
      .finally(() => setLoading(false));
  }, [slug, langParam]);

  if (loading) {
    return (
      <div className="rd-page">
        <div className="rd-container">
          <div className="rd-skeleton-header">
            <div className="skeleton-icon" />
            <div className="skeleton-lines">
              <div className="skeleton-line" style={{ width: '60%' }} />
              <div className="skeleton-line" style={{ width: '40%' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className="rd-page">
        <div className="rd-container">
          <div className="rd-error">
            <h2>404</h2>
            <p>{error || t('resourceNotFound')}</p>
            <Link to="/" className="rd-back-link"><FaArrowLeft /> {t('backToHome')}</Link>
          </div>
        </div>
      </div>
    );
  }

  const fallbackIcon = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(resource.link)}&sz=128`;
  let domain = '';
  try { domain = new URL(resource.link).hostname.replace('www.', ''); } catch {}

  const tags = resource.tags ? resource.tags.split(',').map(t => t.trim()).filter(Boolean) : [];

  return (
    <div className="rd-page">
      <div className="rd-container">
        {/* Breadcrumb */}
        <nav className="rd-breadcrumb">
          <Link to="/">{t('home')}</Link>
          <FaChevronRight />
          {resource.category_name && (
            <>
              <Link to={`/?cat=${resource.category_slug}`}>{resource.category_name}</Link>
              <FaChevronRight />
            </>
          )}
          <span>{resource.name}</span>
        </nav>

        {/* Main card */}
        <div className="rd-main">
          <div className="rd-hero">
            <div className="rd-icon-wrap">
              <img
                src={resource.icon_url || fallbackIcon}
                alt={resource.name}
                onError={(e) => { e.target.src = fallbackIcon; }}
              />
            </div>
            <div className="rd-hero-info">
              <h1 className="rd-title">{resource.name}</h1>
              <div className="rd-meta">
                {resource.category_name && (
                  <span className="rd-category">
                    <FaFolder /> {resource.category_name}
                  </span>
                )}
                <span className="rd-domain">
                  <FaGlobe /> {domain}
                </span>
                <span className="rd-views">
                  <FaEye /> {(resource.views || 0).toLocaleString()} {t('views')}
                </span>
                {resource.created_at && (
                  <span className="rd-date">
                    <FaCalendarAlt /> {new Date(resource.created_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Visit button */}
          <a
            href={resource.link}
            target="_blank"
            rel="noopener noreferrer"
            className="rd-visit-btn"
          >
            <FaExternalLinkAlt /> {t('visitSite')} {resource.name}
            <FaArrowRight className="rd-visit-arrow" />
          </a>

          {/* Description */}
          {resource.description && (
            <div className="rd-section">
              <h2>{t('aboutSite')} {resource.name}</h2>
              <div className="rd-description">
                <p>{resource.description}</p>
              </div>
            </div>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div className="rd-section">
              <h2>{t('tags')}</h2>
              <div className="rd-tags">
                {tags.map(tag => (
                  <span key={tag} className="rd-tag"><FaTag /> {tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* Site info */}
          <div className="rd-section">
            <h2>{t('websiteInfo')}</h2>
            <div className="rd-info-grid">
              <div className="rd-info-item">
                <span className="rd-info-label">{t('domain')}</span>
                <span className="rd-info-value">{domain}</span>
              </div>
              <div className="rd-info-item">
                <span className="rd-info-label">{t('category')}</span>
                <span className="rd-info-value">{resource.category_name || t('uncategorized')}</span>
              </div>
              <div className="rd-info-item">
                <span className="rd-info-label">{t('views')}</span>
                <span className="rd-info-value">{(resource.views || 0).toLocaleString()}</span>
              </div>
              <div className="rd-info-item">
                <span className="rd-info-label">{t('added')}</span>
                <span className="rd-info-value">{resource.created_at ? new Date(resource.created_at).toLocaleDateString() : t('na')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Related resources */}
        {related.length > 0 && (
          <div className="rd-related">
            <h2>{t('relatedResources')}</h2>
            <div className="rd-related-grid">
              {related.map(r => {
                const rIcon = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(r.link)}&sz=64`;
                return (
                  <Link key={r.id} to={`/resource/${r.slug}`} className="rd-related-card">
                    <img
                      src={r.icon_url || rIcon}
                      alt={r.name}
                      onError={(e) => { e.target.src = rIcon; }}
                    />
                    <div>
                      <h4>{r.name}</h4>
                      {r.description && <p>{r.description}</p>}
                    </div>
                    <FaChevronRight className="rd-related-arrow" />
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        <Link to="/" className="rd-back-link"><FaArrowLeft /> {t('backToHome')}</Link>
      </div>
    </div>
  );
}

export default ResourceDetail;
